import { __assign, __read, __rest } from "tslib";
import React, { useRef, useImperativeHandle } from "react";
import classNames from "classnames";
import { SearchIcon } from "@tencent/tea-icons-react";
import { Input } from "../input";
import { Button } from "../button";
import { useDefaultValue } from "../form/controlled";
import { ScaleTransition } from "../transition";
import { useConfig } from "../_util/config-context";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { noop } from "../_util/noop";
import { KeyMap } from "../_util/key-map";
import { callBoth } from "../_util/call-both";
import { mergeRefs } from "../util";
import { useTranslation } from "../i18n";
export var SearchBox = forwardRefWithStatics(function SearchBox(props, ref) {
    var _a, _b;
    var classPrefix = useConfig().classPrefix;
    var t = useTranslation();
    var multiline = props.multiline, simple = props.simple, iconPlacement = props.iconPlacement, _c = props.onSearch, onSearch = _c === void 0 ? noop : _c, _d = props.onClear, onClear = _d === void 0 ? noop : _d, className = props.className, style = props.style, size = props.size, showHelp = props.showHelp, disabled = props.disabled, _e = props.onHelp, onHelp = _e === void 0 ? noop : _e, searchBoxRef = props.searchBoxRef, loading = props.loading, _f = props.hideButton, hideButton = _f === void 0 ? !multiline : _f, // multiline为true时显示按钮，否则隐藏
    _g = props.placeholder, // multiline为true时显示按钮，否则隐藏
    placeholder = _g === void 0 ? t.searchBoxTips : _g, _inputProps = __rest(props, ["multiline", "simple", "iconPlacement", "onSearch", "onClear", "className", "style", "size", "showHelp", "disabled", "onHelp", "searchBoxRef", "loading", "hideButton", "placeholder"]);
    var inputBoxClassNames = classNames((_a = {},
        _a[classPrefix + "-search"] = true,
        _a[classPrefix + "-search--multi"] = multiline,
        _a[classPrefix + "-search--simple"] = simple,
        _a["is-disabled"] = disabled,
        _a[classPrefix + "-search--has-info"] = showHelp,
        _a[className] = className,
        _a));
    var inputProps = useDefaultValue(_inputProps, "");
    var composingRef = useRef(null);
    // 需要使用到 inputRef，同时向外部暴露
    var inputRef = useRef(null);
    useImperativeHandle(ref, function () { return inputRef.current; });
    var _h = __read(React.useState(false), 2), isFocus = _h[0], setIsFocus = _h[1];
    var _j = __read(React.useState(false), 2), isHover = _j[0], setIsHover = _j[1];
    return (React.createElement("div", { className: classNames(classPrefix + "-form--search", (_b = {},
            // [`${classPrefix}-search--has-info`]: showHelp, // 这个为啥放外层了？？？
            _b["size-" + (size === "full" ? size + "-width" : size)] = size,
            _b)), ref: mergeRefs(searchBoxRef) },
        React.createElement("div", { className: inputBoxClassNames, style: style },
            React.createElement("div", { className: classPrefix + "-search__inner" },
                React.createElement("span", { className: classPrefix + "-search__inner-prefix" },
                    React.createElement(SearchIcon, { size: 16 })),
                React.createElement(Input, __assign({ type: "search", placeholder: placeholder, disabled: disabled, multiline: multiline, baseClassName: classPrefix + "-input", className: classPrefix + "-input--search", style: inputProps.value ? null : { paddingRight: "12px" }, onKeyDown: function (event) {
                        var key = event.key, ctrlKey = event.ctrlKey, metaKey = event.metaKey;
                        if (key === KeyMap.Esc) {
                            event.preventDefault();
                            inputProps.onChange("", { event: event });
                            onClear();
                        }
                        if (key === KeyMap.Enter && inputProps.onPressEnter) {
                            inputProps.onPressEnter(event.currentTarget.value, event);
                        }
                        // 回车处理 onSearch 逻辑
                        if (key !== KeyMap.Enter) {
                            return;
                        }
                        // 多行模式下，要按下 ctrl 或者 command
                        if (multiline && !metaKey && !ctrlKey) {
                            return;
                        }
                        event.preventDefault();
                        if (!composingRef.current) {
                            onSearch(event.currentTarget.value, { event: event });
                        }
                    }, onCompositionStart: callBoth(function () {
                        composingRef.current = true;
                    }, inputProps.onCompositionStart), onCompositionEnd: callBoth(function () {
                        composingRef.current = false;
                    }, inputProps.onCompositionEnd), ref: inputRef, onMouseEnter: function (event) {
                        setIsHover(true);
                        if (inputProps.onMouseEnter) {
                            inputProps.onMouseEnter(event);
                        }
                    }, onMouseLeave: function (event) {
                        setIsHover(false);
                        if (inputProps.onMouseLeave) {
                            inputProps.onMouseLeave(event);
                        }
                    }, onFocus: function (event) {
                        setIsFocus(true);
                        if (inputProps.onFocus) {
                            inputProps.onFocus(event);
                        }
                    }, onBlur: function (event) {
                        setIsFocus(false);
                        if (inputProps.onBlur) {
                            inputProps.onBlur(event);
                        }
                    } }, inputProps)),
                React.createElement(ScaleTransition, { in: /\S/.test(inputProps.value) },
                    React.createElement(Button, { icon: "close", htmlType: "button", disabled: disabled, className: classPrefix + "-btn--dismiss", onClick: function (event) {
                            event.preventDefault();
                            inputProps.onChange("", { event: event });
                            onClear();
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        } })),
                showHelp && (React.createElement(Button, { icon: "info", htmlType: "button", disabled: disabled, className: classPrefix + "-btn--info", onClick: onHelp }))),
            !hideButton && (React.createElement(Button, { loading: loading, htmlType: "button", disabled: disabled, className: classPrefix + "-btn--search " + (isFocus || isHover ? "is-focus" : ""), onClick: function (event) {
                    event.preventDefault();
                    onSearch(inputProps.value, { event: event });
                } }, t.searchText)))));
}, {
    defaultLabelAlign: "middle",
});
SearchBox.displayName = "SearchBox";
//# sourceMappingURL=SearchBox.js.map